import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { GoogleMap } from "@angular/google-maps";
import { CarWorkshopService } from "@core/services/car-workshop.service";
import { LabelService, defaultMileageUnit } from "@core/services/label.service";
import { NotifyService } from "@core/services/notify.service";
import { VehicleService } from "@core/services/vehicle.service";
import { environment } from "@env";
import { DEFAULT_PAGINATION } from "@shared/constants/default-pagination";
import { assetUrl } from "@shared/helper/asset-url";
import {
  NearbyCarWorkshopDTO,
  NearbyCarWorkshopFilterDTO,
  NearbyHistoryCarWorkshopDTO
} from "@shared/models/nearby-history-car-workshop-dto";
import { PageManager } from "@shared/models/page-manager";
import { PageFilterDto } from "@shared/page-filter-dto";
import { CurrencyFormatPipe } from "@shared/pipes";

import { Observable, forkJoin, fromEvent, of } from "rxjs";
import { debounceTime, first, map, tap } from "rxjs/operators";

interface CustomMarker {
  position: google.maps.LatLngLiteral;
  options: google.maps.MarkerOptions;
  carworkshop: NearbyHistoryCarWorkshopDTO;
}

@Component({
  selector: "app-smartfleet-find-carworkshop-maps",
  templateUrl: "./find-carworkshop-maps.component.html",
  styleUrls: ["./find-carworkshop-maps.component.scss"],
  standalone: false
})
export class FindCarworkshopMapsComponent implements OnInit {
  @ViewChild("search") public searchRef: ElementRef;
  @ViewChild(GoogleMap) map: GoogleMap;

  @Input() clientId: number;
  @Input() vehicleId: number;
  @Input() serviceType: string;
  @Input() serviceCategories: any = null;
  @Input() servicesProvided: any = null;
  @Input() kmPackage: number;
  @Input() maintenancePlanPackageId: number | null = null;
  @Input() clientIsNewPreventiveFlow: boolean = false;
  @Input() observations;
  @Input() vehicleDefects;

  @Output() onChange: EventEmitter<any> = new EventEmitter();

  center: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  zoom = 1;
  mapOptions: google.maps.MapOptions = {
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 1
  };

  address = "";
  private geoCoder: any;
  nearbiesCarworkshop: NearbyHistoryCarWorkshopDTO[] = [];
  historyCarworkshop: NearbyHistoryCarWorkshopDTO[] = [];
  loadingCarworkshops = false;
  searched = false;
  selectedCarworkshopId = "";
  searchPlaceInput = new FormControl("", [Validators.required]);
  autocomplete: google.maps.places.AutocompleteService;
  networkListInput = new FormControl([]);

  isLastPaginationNearby = false;
  isLoadingNearby = false;

  pageManager: PageManager = {
    totalElements: 0,
    pageIndex: DEFAULT_PAGINATION.PAGE_INDEX,
    pageSize: 5
  };

  mileageUnit = defaultMileageUnit;

  userMarker: CustomMarker;
  carworkshopMarkers: CustomMarker[] = [];

  pinCarworkshop = assetUrl("img/icons-driveb/workshop_marker.png");
  pinUser = assetUrl("img/icons-driveb/my_location_marker.png");
  selectedPinCarworkshop = assetUrl(
    "img/icons-driveb/selected_workshop_marker.png"
  );

  results$: Observable<any[]>;
  loadingMap: boolean;
  isGoogleMapsLoaded = false;

  constructor(
    private ngZone: NgZone,
    private carWorkshopService: CarWorkshopService,
    private vehicleService: VehicleService,
    private notifyService: NotifyService,
    private labelService: LabelService,
    private carworkshopService: CarWorkshopService,
    private currencyFormatPipe: CurrencyFormatPipe
  ) {}

  async ngOnInit() {
    this.mileageUnit = this.labelService.getMileageUnit();

    await this.loadGoogleMaps();

    this.geoCoder = new google.maps.Geocoder();

    const getPlaces = google.maps.importLibrary(
      "places"
    ) as Promise<google.maps.PlacesLibrary>;
    const { AutocompleteService } = await getPlaces;

    this.autocomplete = new AutocompleteService();

    fromEvent(this.searchRef.nativeElement, "keyup")
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.onSearch(this.searchPlaceInput.value);
      });
  }

  private async loadGoogleMaps(): Promise<void> {
    if (typeof google === "undefined" || typeof google.maps === "undefined") {
      await this.loadGoogleMapsScript();
    }
    this.isGoogleMapsLoaded = true;
    this.setCurrentLocation();
  }

  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
  }

  onSearch(input: string) {
    this.loadingMap = true;
    const filter: PageFilterDto = {
      page: 0,
      pageSize: this.pageManager.pageSize
    };

    const body: NearbyCarWorkshopDTO = this.getBody(input, input);

    this.autocomplete.getPlacePredictions({ input }, (res) => {
      const carworkshops$ = this.carworkshopService
        .findByMap(filter, body)
        .pipe(
          first(),
          map((res) => res.content)
        );

      this.results$ = forkJoin([
        of(res).pipe(map(this.formatDescriptionForAutocomplete)),
        carworkshops$.pipe(map(this.formatDescriptionForAutocomplete))
      ]).pipe(
        map(([res, carworkshops]) => {
          return carworkshops.concat(res || []);
        }),
        tap(() => (this.loadingMap = false))
      );
    });
  }

  get networkList(): string[] {
    return this.networkListInput.value;
  }

  private setCurrentLocation() {
    if ("geolocation" in navigator && this.isGoogleMapsLoaded) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.zoom = 8;

        this.userMarker = {
          position: this.center,
          options: {
            icon: {
              url: this.pinUser,
              scaledSize: new google.maps.Size(40, 40)
            }
          },
          carworkshop: null
        };

        this.getAddress(this.center.lat, this.center.lng);
      });
    }
  }

  async placeChanged(placeSelected: any | NearbyHistoryCarWorkshopDTO) {
    this.ngZone.run(async () => {
      if (!placeSelected.place_id) {
        return;
      }

      if (placeSelected.type === "carworkshop") {
        const split = placeSelected.description.split(" - ");
        const input = split[split.length - 1];
        this.autocomplete.getPlacePredictions({ input }, async (res) => {
          if (res.length > 0) {
            await this.getPlaceDetails(res[0].place_id, placeSelected);
          }
          return;
        });

        return;
      }

      this.getPlaceDetails(placeSelected.place_id);
    });
  }

  getCarWorkshopsNearby(resetPagination = false) {
    this.isLoadingNearby = true;

    if (resetPagination) {
      this.nearbiesCarworkshop = [];
      this.carworkshopMarkers = [];
      this.pageManager.pageIndex = DEFAULT_PAGINATION.PAGE_INDEX;
      this.pageManager.totalElements = 0;
    }

    const filter = this.getFilterForCarWorkshopNearby();
    const body: NearbyCarWorkshopDTO = this.getBody(
      this.searchPlaceInput.value,
      null
    );

    this.carWorkshopService
      .getPaginatedCarWorkshopsNearby(filter, body)
      .subscribe(
        (data) => {
          this.isLastPaginationNearby = data.last;
          this.pageManager.totalElements = data.totalElements;

          const newCarworkshops = data.content.map((c) => {
            return {
              ...c,
              maintenancePrice: c.maintenancePrice
                ? this.currencyFormatPipe.transform(Number(c.maintenancePrice))
                : null
            };
          });

          this.nearbiesCarworkshop.push(...newCarworkshops);

          newCarworkshops.forEach((carworkshop) => {
            this.carworkshopMarkers.push({
              position: {
                lat: carworkshop.latitude,
                lng: carworkshop.longitude
              },
              options: {
                icon: {
                  url: this.getCustomPinMap(carworkshop.id.toString()),
                  scaledSize: new google.maps.Size(40, 40)
                }
              },
              carworkshop: carworkshop
            });
          });

          this.pageManager.pageIndex += 1;
          this.isLoadingNearby = false;
        },
        (error) => {
          this.isLoadingNearby = false;
          this.notifyService.showErrorMessageWithTranslationKey(
            "find-carworkshop-maps.something-wrong-enter-valid-filter",
            error,
            "Error to get nearby carworkshops"
          );
        }
      );
  }

  async getCarworkshopsByHistory() {
    try {
      const filter = this.getFilters();
      this.historyCarworkshop = await this.vehicleService
        .getCarworkshopsByHistory(
          this.vehicleId,
          filter,
          this.maintenancePlanPackageId
        )
        .pipe(
          map((history) => {
            return history.map((c) => {
              return {
                ...c,
                maintenancePrice: c.maintenancePrice
                  ? this.currencyFormatPipe.transform(
                      Number(c.maintenancePrice)
                    )
                  : null
              };
            });
          })
        )
        .toPromise();
    } catch (error) {
      this.notifyService.showErrorMessageWithTranslationKey(
        "find-carworkshop-maps.something-wrong-enter-valid-filter",
        error,
        "Error to get history carworkshops"
      );
    }
  }

  getAddress(latitude: number, longitude: number) {
    this.geoCoder.geocode(
      { location: { lat: latitude, lng: longitude } },
      (results: any, status: any) => {
        if (status === "OK") {
          if (results[0]) {
            this.zoom = 12;
            this.searchPlaceInput.setValue(results[0].formatted_address);
            this.placeChanged(results[0]);
          } else {
            this.notifyService.showErrorMessageWithTranslationKey(
              "default-error-message",
              "Error to get history and nearby carworkshops"
            );
          }
        }
      }
    );
  }

  selectCarworkshop(carworkshop) {
    this.selectedCarworkshopId = carworkshop.id;

    this.carworkshopMarkers = this.carworkshopMarkers.map((marker) => {
      const isSelected = marker.carworkshop?.id === carworkshop.id;
      return {
        ...marker,
        options: {
          icon: {
            url: isSelected ? this.selectedPinCarworkshop : this.pinCarworkshop,
            scaledSize: new google.maps.Size(40, 40)
          }
        }
      };
    });

    this.onChange.emit(carworkshop);
  }

  onMarkerClick(marker: CustomMarker) {
    if (marker.carworkshop) {
      this.selectCarworkshop(marker.carworkshop);
    }
  }

  getFilters(): NearbyCarWorkshopFilterDTO {
    const filter: any = {
      latitude: this.center.lat,
      longitude: this.center.lng,
      serviceType: this.serviceType,
      vehicleId: this.vehicleId,
      page: this.pageManager.pageIndex,
      pageSize: this.pageManager.pageSize,
      kmPackage: this.kmPackage
    };

    if (this.networkList && this.networkList.length) {
      filter.networkList = this.networkList;
    }

    if (this.serviceCategories) {
      filter.serviceCategories = this.serviceCategories;
    }

    if (this.servicesProvided && this.servicesProvided.length) {
      filter.servicesProvided = this.servicesProvided;
    }

    Object.keys(filter).forEach((key) => {
      const value = filter[key];
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete filter[key];
      }
    });

    return filter;
  }

  onScroll(event: any) {
    if (this.isLoadingNearby) return;

    const target = event.target;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const pageHeight = target.scrollHeight;

    const isEnd = scrollPosition >= pageHeight - 50;

    if (!isEnd || this.isLastPaginationNearby) return;

    this.getCarWorkshopsNearby();
  }

  handleNetworkSelection(updatedNetworkList) {
    this.networkListInput.setValue(updatedNetworkList);
    this.getCarWorkshopsNearby(true);
    this.onChange.emit({ id: null });
    this.selectedCarworkshopId = null;
  }

  getCustomPinMap(carworkshopId: string): string {
    return carworkshopId === this.selectedCarworkshopId
      ? this.selectedPinCarworkshop
      : this.pinCarworkshop;
  }

  private formatDescriptionForAutocomplete(object) {
    return object?.map((r) => {
      const description =
        r.description ||
        `${r.tradingName} - ${r.address.street}, ${r.address.number}, ${r.address.city}, ${r.address.state}, ${r.address.country}`;

      return {
        place_id: r.place_id || r.id,
        description: description,
        type: r.tradingName ? "carworkshop" : "mappoint",
        address: r.address,
        id: r.id,
        latitude: r.latitude,
        longitude: r.longitude,
        tradingName: r.description || r.tradingName
      } as unknown;
    });
  }

  private getPlaceDetails(
    placeId: string,
    carworkshop?: NearbyHistoryCarWorkshopDTO
  ) {
    let place: any;
    var placesService = new google.maps.places.PlacesService(
      this.searchRef.nativeElement
    );

    placesService.getDetails({ placeId }, async (results) => {
      place = results;

      if (place.geometry === undefined || place.geometry === null) {
        return;
      }

      this.center = {
        lat: carworkshop?.latitude || place.geometry.location.lat(),
        lng: carworkshop?.longitude || place.geometry.location.lng()
      };

      this.zoom = 15;
      this.loadingCarworkshops = true;
      this.loadingMap = true;
      this.searched = true;

      this.nearbiesCarworkshop = [];
      this.carworkshopMarkers = [];
      this.historyCarworkshop = [];

      const promises = [
        this.getCarWorkshopsNearby(true),
        this.getCarworkshopsByHistory()
      ];

      await Promise.all(promises).then(() => {
        this.loadingMap = false;
        this.loadingCarworkshops = false;

        if (carworkshop) {
          this.selectCarworkshop(carworkshop);
        }
      });
    });
  }

  private getBody(
    searchTerm: string,
    tradingName: string | null
  ): NearbyCarWorkshopDTO {
    const body: NearbyCarWorkshopDTO = {
      latitude: this.center.lat,
      longitude: this.center.lng,
      serviceType: this.serviceType,
      vehicleId: this.vehicleId,
      maintenancePlanPackageId: this.maintenancePlanPackageId,
      kmPackage: this.kmPackage,
      tradingName: tradingName,
      searchTerm: searchTerm,
      observations: this.observations,
      vehicleDefects: this.vehicleDefects
    };

    if (this.networkList && this.networkList.length) {
      body.networkList = this.networkList;
    }

    if (this.serviceCategories) {
      body.serviceCategories = [this.serviceCategories];
    }

    if (this.servicesProvided && this.servicesProvided.length) {
      body.servicesProvided = this.servicesProvided;
    }

    return body;
  }

  private getFilterForCarWorkshopNearby(): NearbyCarWorkshopFilterDTO {
    return {
      page: this.pageManager.pageIndex,
      pageSize: this.pageManager.pageSize
    } as NearbyCarWorkshopFilterDTO;
  }
}
